/**
 * @retail-ops/api-client/errors
 *
 * ApiError class + classifyError helper.
 * Error envelopes recognized:
 *   - Domain `/accounts/*` PascalCase        { Success:false, ErrorCode, Error }
 *   - v3 camelCase                            { success:false, error, errorCode?, errors? }
 *   - jXRL raw                                { ResponseHeader_CType: { StatusCode, Severity }, Details:[] }
 *   - WAF block                               { ResponseCode:"WAF", Description, Details:[] }
 *   - SignNow                                 { Success:false, Error, FailurePhase, RolledBackDocIds }
 *   - Dataverse                               { Success:false, Error, ErrorCode }
 *   - Wet-sign 422 shape (distinct)           { success:false, reason, error }
 */

import type { ErrorCategory } from '@retail-ops/shared-types';

export type { ErrorCategory };

/**
 * Thrown by ApiHttpClient.request() on any non-success response.
 * Components catch this; category drives UX decisions (retry/auth/ratelimit/etc).
 */
export class ApiError extends Error {
  public readonly httpStatus: number;
  public readonly category: ErrorCategory;
  public readonly code?: string;
  public readonly details?: unknown;
  public readonly retryAfter?: number;
  public readonly traceparent?: string;
  public readonly correlationId?: string;

  public constructor(init: {
    httpStatus: number;
    category: ErrorCategory;
    message: string;
    code?: string;
    details?: unknown;
    retryAfter?: number;
    traceparent?: string;
    correlationId?: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.httpStatus = init.httpStatus;
    this.category = init.category;
    this.code = init.code;
    this.details = init.details;
    this.retryAfter = init.retryAfter;
    this.traceparent = init.traceparent;
    this.correlationId = init.correlationId;
    // Restore prototype chain for ES5 targets / tooling that strips it.
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /** Narrow guard for consumers that receive `unknown` from promise rejections. */
  public static isApiError(e: unknown): e is ApiError {
    return e instanceof ApiError || (typeof e === 'object' && e !== null && (e as { name?: string }).name === 'ApiError');
  }
}

// ---------------------------------------------------------------------------
// classifyError
// ---------------------------------------------------------------------------

/**
 * Maps an HTTP status + parsed body to an ErrorCategory.
 * Treats jXRL/SignNow/Dataverse domain 4xx as `upstream` (not `validation`)
 * when the upstream ID/envelope is recognized; v3 422 bodies stay `validation`.
 */
export function classifyError(httpStatus: number, body: unknown): ErrorCategory {
  if (httpStatus === 401 || httpStatus === 403) {
    // 403 may also be WAF block — upstream on the WAF envelope, but category is still auth
    // because tokens are usually the corrective action.
    if (isWafBlock(body)) return 'upstream';
    return 'auth';
  }
  if (httpStatus === 429) return 'ratelimit';
  if (httpStatus === 422) {
    // jXRL/SignNow/Dataverse upstream validation bubbles here as well
    if (isJxrlErrorEnvelope(body) || isSignNowError(body) || isDataverseError(body)) {
      return 'upstream';
    }
    return 'validation';
  }
  if (httpStatus === 400 || httpStatus === 409 || httpStatus === 412) return 'validation';
  if (httpStatus === 502 || httpStatus === 503 || httpStatus === 504) return 'upstream';
  if (httpStatus >= 500) return 'unknown';
  if (httpStatus === 0) return 'network';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// Envelope detectors
// ---------------------------------------------------------------------------

function hasProp<K extends string>(obj: unknown, key: K): obj is Record<K, unknown> {
  return typeof obj === 'object' && obj !== null && key in (obj as object);
}

export function isWafBlock(body: unknown): boolean {
  return hasProp(body, 'ResponseCode') && (body as { ResponseCode: unknown }).ResponseCode === 'WAF';
}

export function isJxrlErrorEnvelope(body: unknown): boolean {
  if (!hasProp(body, 'ResponseHeader_CType')) return false;
  const header = (body as { ResponseHeader_CType: unknown }).ResponseHeader_CType;
  return hasProp(header, 'Severity') && (header as { Severity: unknown }).Severity === 'Error';
}

export function isSignNowError(body: unknown): boolean {
  // The v3 Function serializes StartSigningResponse with
  // JsonNamingPolicy.CamelCase, so the wire shape is camelCase
  // (success / failurePhase / rolledBackDocIds) — NOT the PascalCase this
  // predicate originally checked, which meant it never matched and the
  // SignNow-specific failurePhase code was silently lost (BACKLOG-0748).
  return (
    hasProp(body, 'success') &&
    (body as { success: unknown }).success === false &&
    (hasProp(body, 'failurePhase') || hasProp(body, 'rolledBackDocIds'))
  );
}

export function isDataverseError(body: unknown): boolean {
  return (
    hasProp(body, 'Success') &&
    (body as { Success: unknown }).Success === false &&
    hasProp(body, 'ErrorCode') &&
    typeof (body as { ErrorCode: unknown }).ErrorCode === 'string' &&
    ((body as { ErrorCode: string }).ErrorCode.startsWith('0x') ||
      /bt_/i.test(String((body as { Error?: unknown }).Error ?? '')))
  );
}

export function isWetSignFailure(body: unknown): boolean {
  return (
    hasProp(body, 'success') &&
    (body as { success: unknown }).success === false &&
    hasProp(body, 'reason') &&
    typeof (body as { reason: unknown }).reason === 'string'
  );
}

export function isDomainPascalError(body: unknown): boolean {
  return (
    hasProp(body, 'Success') &&
    (body as { Success: unknown }).Success === false &&
    (hasProp(body, 'ErrorCode') || hasProp(body, 'Error'))
  );
}

export function isCamelError(body: unknown): boolean {
  return (
    hasProp(body, 'success') &&
    (body as { success: unknown }).success === false &&
    (hasProp(body, 'error') || hasProp(body, 'errors'))
  );
}

// ---------------------------------------------------------------------------
// Build ApiError from a response body
// ---------------------------------------------------------------------------

export interface BuildApiErrorInput {
  httpStatus: number;
  body: unknown;
  traceparent?: string;
  correlationId?: string;
  retryAfterHeader?: string | null;
  fallbackMessage?: string;
}

export function buildApiError(input: BuildApiErrorInput): ApiError {
  const { httpStatus, body, traceparent, correlationId, retryAfterHeader, fallbackMessage } = input;

  const category = classifyError(httpStatus, body);
  let code: string | undefined;
  let message = fallbackMessage ?? `HTTP ${httpStatus}`;

  if (isDomainPascalError(body)) {
    const b = body as { ErrorCode?: string; Error?: string; ErrorMessage?: string };
    code = b.ErrorCode;
    message = b.Error ?? b.ErrorMessage ?? message;
  } else if (isSignNowError(body)) {
    // Check BEFORE isCamelError — the SignNow camel response also satisfies
    // isCamelError (success:false + error), but only this branch preserves the
    // SignNow failurePhase as the error code (BACKLOG-0748).
    const b = body as { error?: string; failurePhase?: string };
    code = b.failurePhase;
    message = b.error ?? message;
  } else if (isCamelError(body)) {
    const b = body as { errorCode?: string; error?: string };
    code = b.errorCode;
    message = b.error ?? message;
  } else if (isJxrlErrorEnvelope(body)) {
    const b = body as { ResponseHeader_CType?: { StatusCode?: string; StatusDesc?: string } };
    code = b.ResponseHeader_CType?.StatusCode;
    message = b.ResponseHeader_CType?.StatusDesc ?? message;
  } else if (isWafBlock(body)) {
    code = 'WAF';
    message = (body as { Description?: string }).Description ?? 'WAF block';
  } else if (isDataverseError(body)) {
    const b = body as { ErrorCode?: string; Error?: string };
    code = b.ErrorCode;
    message = b.Error ?? message;
  } else if (isWetSignFailure(body)) {
    const b = body as { reason?: string; error?: string };
    code = b.reason;
    message = b.error ?? message;
  }

  const retryAfter = retryAfterHeader != null ? parseRetryAfter(retryAfterHeader) : undefined;

  return new ApiError({
    httpStatus,
    category,
    message,
    code,
    details: body,
    retryAfter,
    traceparent,
    correlationId,
  });
}

function parseRetryAfter(value: string): number | undefined {
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  // HTTP-date form
  const date = Date.parse(value);
  if (!Number.isNaN(date)) {
    const diffMs = date - Date.now();
    return diffMs > 0 ? Math.round(diffMs / 1000) : 0;
  }
  return undefined;
}
